//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import DefaultModal from '@components/DefaultModal';
import { RouteAnchor } from '@components/RouteAnchor';
import Button, {
  TertiaryButton,
  getButtonStyle,
} from '@components/atoms/button/Button';
import {
  PrimaryButtonStyles,
  TertiaryButtonStyles,
} from '@components/atoms/button/buttonStyles';
import RequiredPermissionsButton from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { NestedCheckboxItem } from '@media-library/components/top-bar/DropdownNestedCheckbox';
import { NonBusinessTooltip } from '@media-library/components/top-bar/NonBusinessTooltip';
import { MediaLibrarySourcesDropdownItemData } from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownConstants';
import {
  filterSources,
  sortBySelectedAndLabel,
} from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownUtils';
import { hasPermissionForAllTasks } from '@services/permissions';
import { isDefined } from '@utils/filterPredicates';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface TertiaryButtonInfo extends ITrackableComponent {
  content: string | React.ReactNode;
  permissionName?: string;
  state: React.ComponentProps<typeof RouteAnchor>['state'];
}

interface SourcesModalProps {
  applyButtonTrackingId: string;
  cancelButtonTrackingId: string;
  handleChangeSelects: (
    updatedSources: MediaLibrarySourcesDropdownItemData,
  ) => void;
  handleCloseModal: () => void;
  isOpen: boolean;
  searchBarPlaceholder: string;
  searchBarTrackingIdPrefix: string;
  searchBarVisuallyHiddenLabel: string;
  sourcesGroup: MediaLibrarySourcesDropdownItemData;
  tertiaryButtonInfo: TertiaryButtonInfo;
  title: string;
}

export const SourcesModal = ({
  applyButtonTrackingId,
  cancelButtonTrackingId,
  handleChangeSelects,
  handleCloseModal,
  isOpen,
  searchBarPlaceholder,
  searchBarTrackingIdPrefix,
  searchBarVisuallyHiddenLabel,
  sourcesGroup,
  tertiaryButtonInfo,
  title,
}: SourcesModalProps) => {
  const subItems = sourcesGroup.subItems ?? [];
  // Sort by label in alphabetical order.
  const optionsSortedByLabel = [...subItems].sort((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
  );
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [options, setOptions] = useState(optionsSortedByLabel);

  const hasPermission = hasPermissionForAllTasks(
    isDefined(tertiaryButtonInfo.permissionName)
      ? [tertiaryButtonInfo.permissionName]
      : [],
  );

  const handleApply = () => {
    handleChangeSelects({
      ...sourcesGroup,
      isSelected: options.every(({ isSelected }) => isSelected),
      isIndeterminate: options.some(({ isSelected }) => isSelected),
      subItems: sortBySelectedAndLabel([...options]),
    });
    handleCloseModal();
  };

  return (
    <StyledModal
      closeModal={handleCloseModal}
      contents={
        <ModalContent>
          <ModalBody>
            <SearchBar
              autoFocus={true}
              handleClear={() => setSearchTerm('')}
              onChange={setSearchTerm}
              placeholder={searchBarPlaceholder}
              trackingIdPrefix={searchBarTrackingIdPrefix}
              value={searchTerm}
              visuallyHiddenLabel={searchBarVisuallyHiddenLabel}
            />

            <SourcesSelectionPanel
              onChangeSelect={(updatedOption) => {
                const option = options.find(
                  ({ value }) => value === updatedOption.value,
                );

                if (isDefined(option)) {
                  option.isIndeterminate = updatedOption.isIndeterminate;
                  option.isSelected = updatedOption.isSelected;
                  setOptions([...options]);
                }
              }}
              selectOptions={options.filter((option) =>
                filterSources(option, searchTerm),
              )}
            />
          </ModalBody>

          <ModalFooter>
            {isDefined(tertiaryButtonInfo) &&
              (tertiaryButtonInfo.permissionName && !hasPermission ? (
                <RequiredPermissionsButton
                  isDisabled={!hasPermission}
                  permissionName={tertiaryButtonInfo.permissionName}
                  trackingId={tertiaryButtonInfo.trackingId}
                >
                  {tertiaryButtonInfo.content}
                </RequiredPermissionsButton>
              ) : (
                <Anchor
                  state={tertiaryButtonInfo.state}
                  target='_blank'
                  trackingId={tertiaryButtonInfo.trackingId}
                >
                  {tertiaryButtonInfo.content}
                </Anchor>
              ))}

            <MainActionsWrapper>
              <TertiaryButton
                onClick={handleCloseModal}
                trackingId={cancelButtonTrackingId}
              >
                Cancel
              </TertiaryButton>

              <Button
                buttonStyles={PrimaryButtonStyles}
                onClick={handleApply}
                trackingId={applyButtonTrackingId}
              >
                Apply
              </Button>
            </MainActionsWrapper>
          </ModalFooter>
        </ModalContent>
      }
      isFooterEnabled={false}
      isModalOpen={isOpen}
      shouldIncludeTertiaryButton={isDefined(tertiaryButtonInfo)}
      title={title}
      width='wide'
    />
  );
};

interface SourcesSelectionPanelProps {
  onChangeSelect: (option: MediaLibrarySourcesDropdownItemData) => void;
  selectOptions: MediaLibrarySourcesDropdownItemData[];
}

const SourcesSelectionPanel = ({
  onChangeSelect,
  selectOptions,
}: SourcesSelectionPanelProps) => {
  return (
    <Labels>
      {selectOptions.map((option) => (
        <li key={`${option.value}-${option.label}`}>
          <NestedCheckboxItem
            handleChangeSelect={onChangeSelect}
            item={option}
            rightComponent={
              option.paramKey === 'trackedUserIds' ? (
                <NonBusinessTooltip />
              ) : undefined
            }
          />
        </li>
      ))}
    </Labels>
  );
};

const StyledModal = styled(DefaultModal)(
  ({ theme }) => `
  height: ${theme.wideMeasure};
`,
);

const ModalContent = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacings[3]};
  height: 100%;
  width: 100%;
  // Height of modal minus height of title and body's padding-bottom.
  max-height: calc(${theme.wideMeasure} - ${theme.spacings[5]} - ${theme.spacings[1]} - ${theme.spacings[4]} - ${theme.spacings[4]} - ${theme.spacings[5]});
  overflow-y: hidden;
`,
);

const ModalBody = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[3]};
  width: 100%;
  overflow-y: hidden;
`,
);

const ModalFooter = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding-top: ${theme.spacings[4]};
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
  `,
);

const MainActionsWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  gap: ${theme.spacings[2]};
  margin-left: auto;
`,
);

const Anchor = withTracking(
  styled(RouteAnchor)(
    ({ theme }) => css`
      ${getButtonStyle({ theme, buttonStyles: TertiaryButtonStyles })}
      display: flex;
      align-items: center;
      justify-content: center;
      padding: ${theme.spacings[2]} ${theme.spacings[3]};
      color: ${TertiaryButtonStyles.standard.color} !important;
      font-weight: ${theme.fontWeights.semiBold};
      text-decoration: none !important;

      &:hover {
        color: ${TertiaryButtonStyles.hover.color} !important;
      }
    `,
  ),
);

const Labels = styled.ul(
  ({ theme }) => `
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${theme.spacings[2]};
    align-items: start;
    width: 100%;
    padding: ${theme.spacings[0]};
    overflow-y: auto;
    list-style: none;
  `,
);
